'use client';

import { useSelectedLayoutSegment } from 'next/navigation';
import { type ReactElement, type ReactNode, createContext } from 'react';

import { BasePageParams } from '@/app/urls/url-mapping';
import type { DatoLocale } from '@/lib/datocms';
import { urlToDatoLocale } from '@/lib/utils';
import type { Language, Region } from '@/types';

export type LanguageOption = { language: Language; path: string };
export type BasePageData = {
  gaPageType: string;
  gaCategory: string;
  languageOptions: LanguageOption[];
};
export type PageDataContext<
  TCmsData extends Record<string, unknown>,
  TPageData extends Record<string, unknown>
> = {
  params: {
    language: Language;
    region: Region;
    pathParts?: string[];
    slug?: string;
    page?: string;
  };
  locale: DatoLocale;
  cmsData: TCmsData;
  pageData: TPageData & BasePageData;
};

export type PageContextProviderProps<
  TCmsData extends Record<string, unknown>,
  TPageData extends Record<string, unknown>
> = {
  children: ReactNode;
  pageContext: PageDataContext<TCmsData, TPageData>;
};

export const PageContext = createContext<PageDataContext<
  Record<string, unknown>,
  Record<string, unknown>
> | null>(null);

export function PageContextProvider<
  TCmsData extends Record<string, unknown>,
  TPageData extends Record<string, unknown>
>(props: PageContextProviderProps<TCmsData, TPageData>): ReactElement {
  const { children, pageContext } = props;
  return (
    <PageContext.Provider value={pageContext}>{children}</PageContext.Provider>
  );
}

export function SimplePageContextProvider(
  props: Pick<BasePageParams, 'region' | 'language'> & { children: ReactNode }
) {
  const { children, language, region } = props;
  const page = useSelectedLayoutSegment() || '';

  return (
    <PageContextProvider
      pageContext={{
        params: { language, region },
        locale: urlToDatoLocale[`${region}${language}`],
        cmsData: {},
        pageData: {
          gaPageType: page,
          gaCategory: '',
          languageOptions: [],
        },
      }}
    >
      {children}
    </PageContextProvider>
  );
}
